import { useState, useEffect, useCallback } from 'react';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/ui/use-toast';

export interface WebAuthnCredential {
  id: string;
  user_id: string;
  credential_id: string;
  credential_name: string | null;
  device_type: string | null;
  authenticator_type: string | null;
  rp_id: string | null;
  device_id: string | null;
  created_at: string;
  last_used_at: string | null;
}

export interface SecuritySettings {
  biometric_enabled: boolean;
  biometric_unlock_enabled: boolean;
  biometric_login_enabled: boolean;
  passkey_enabled: boolean;
  lock_on_resume_enabled: boolean;
  lock_grace_period_seconds: number;
}

// Check if WebAuthn is supported
export const isWebAuthnSupported = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    !!window.PublicKeyCredential &&
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'
  );
};

// Check if platform authenticator (Face ID/Touch ID) is available
export const isPlatformAuthenticatorAvailable = async (): Promise<boolean> => {
  if (!isWebAuthnSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

export const useBiometricAuth = () => {
  const { user } = useAuth();
  const [credentials, setCredentials] = useState<WebAuthnCredential[]>([]);
  const [settings, setSettings] = useState<SecuritySettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSupported, setIsSupported] = useState(false);
  const [hasPlatformAuth, setHasPlatformAuth] = useState(false);

  // Check browser support on mount
  useEffect(() => {
    const checkSupport = async () => {
      setIsSupported(isWebAuthnSupported());
      setHasPlatformAuth(await isPlatformAuthenticatorAvailable());
    };
    checkSupport();
  }, []);

  // Fetch credentials and settings
  const fetchData = useCallback(async () => {
    if (!user) {
      setCredentials([]);
      setSettings(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch credentials
      const { data: credData, error: credError } = await supabase
        .from('webauthn_credentials')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (credError) throw credError;
      setCredentials(credData || []);

      // Fetch or create security settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('user_security_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError;
      }

      if (settingsData) {
        setSettings({
          biometric_enabled: settingsData.biometric_enabled,
          biometric_unlock_enabled: settingsData.biometric_unlock_enabled,
          biometric_login_enabled: settingsData.biometric_login_enabled,
          passkey_enabled: settingsData.passkey_enabled,
          lock_on_resume_enabled: settingsData.lock_on_resume_enabled ?? false,
          lock_grace_period_seconds: settingsData.lock_grace_period_seconds ?? 60,
        });
      } else {
        // Default settings
        setSettings({
          biometric_enabled: false,
          biometric_unlock_enabled: false,
          biometric_login_enabled: false,
          passkey_enabled: false,
          lock_on_resume_enabled: false,
          lock_grace_period_seconds: 60,
        });
      }
    } catch (error) {
      console.error('Error fetching biometric data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Register a new passkey
  const registerPasskey = useCallback(async (credentialName?: string): Promise<boolean> => {
    if (!user || !isSupported) {
      toast.error('Passkeys are not supported on this device');
      return false;
    }

    try {
      // Get registration challenge from server
      const { data: challengeData, error: challengeError } = await supabase.functions.invoke('webauthn-register', {
        body: { action: 'get-challenge', hostname: window.location.hostname }
      });

      if (challengeError || !challengeData) {
        throw new Error(challengeError?.message || 'Failed to get registration challenge');
      }

      // Start WebAuthn registration
      const credential = await startRegistration({
        optionsJSON: challengeData
      });

      // Determine authenticator type from response
      let authenticatorType = 'Passkey';
      if (credential.authenticatorAttachment === 'platform') {
        // Try to determine if it's Face ID or Touch ID based on platform
        const ua = navigator.userAgent.toLowerCase();
        if (ua.includes('mac') || ua.includes('iphone') || ua.includes('ipad')) {
          authenticatorType = ua.includes('iphone') || ua.includes('ipad') ? 'Face ID' : 'Touch ID';
        } else if (ua.includes('windows')) {
          authenticatorType = 'Windows Hello';
        } else if (ua.includes('android')) {
          authenticatorType = 'Fingerprint';
        }
      }

      // Verify and store credential on server
      const { error: verifyError } = await supabase.functions.invoke('webauthn-register', {
        body: {
          action: 'verify',
          credential: {
            id: credential.id,
            publicKey: credential.response.publicKey,
            counter: 0,
            transports: credential.response.transports,
          },
          credentialName: credentialName || authenticatorType,
          deviceType: credential.authenticatorAttachment || 'platform',
          authenticatorType,
        }
      });

      if (verifyError) {
        throw new Error(verifyError.message || 'Failed to register passkey');
      }

      toast.success('Passkey registered successfully');
      await fetchData(); // Refresh data
      return true;
    } catch (error: any) {
      console.error('Passkey registration error:', error);

      // Handle user cancellation
      if (error.name === 'NotAllowedError') {
        toast.error('Passkey registration was cancelled');
      } else {
        toast.error(error.message || 'Failed to register passkey');
      }
      return false;
    }
  }, [user, isSupported, fetchData]);

  // Authenticate with passkey (for login)
  const authenticateWithPasskey = useCallback(async (email: string): Promise<{ success: boolean; url?: string }> => {
    if (!isSupported) {
      toast.error('Passkeys are not supported on this device');
      return { success: false };
    }

    try {
      // Get authentication challenge
      const { data: challengeData, error: challengeError } = await supabase.functions.invoke('webauthn-authenticate', {
        body: { action: 'get-challenge', email, hostname: window.location.hostname }
      });

      if (challengeError || !challengeData) {
        throw new Error(challengeError?.message || 'Failed to get authentication challenge');
      }

      // Start WebAuthn authentication
      const credential = await startAuthentication({
        optionsJSON: challengeData
      });

      // Verify credential on server
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('webauthn-authenticate', {
        body: {
          action: 'verify',
          credentialId: credential.id,
          authenticatorData: credential.response.authenticatorData,
          clientDataJSON: credential.response.clientDataJSON,
          signature: credential.response.signature,
          hostname: window.location.hostname,
        }
      });

      if (verifyError || !verifyData?.success) {
        throw new Error(verifyError?.message || 'Authentication failed');
      }

      return { success: true, url: verifyData.url };
    } catch (error: any) {
      console.error('Passkey authentication error:', error);

      if (error.name === 'NotAllowedError') {
        toast.error('Authentication was cancelled');
      } else if (error.message?.includes('No passkeys')) {
        toast.error('No passkeys registered for this account');
      } else {
        toast.error(error.message || 'Authentication failed');
      }
      return { success: false };
    }
  }, [isSupported]);

  // Biometric unlock (for session continuation)
  const biometricUnlock = useCallback(async (): Promise<boolean> => {
    if (!user || !isSupported || credentials.length === 0) {
      return false;
    }

    try {
      // Get unlock challenge
      const { data: challengeData, error: challengeError } = await supabase.functions.invoke('webauthn-authenticate', {
        body: { action: 'get-challenge', email: user.email, hostname: window.location.hostname }
      });

      if (challengeError || !challengeData) {
        throw new Error('Failed to get unlock challenge');
      }

      // Authenticate with biometrics
      const credential = await startAuthentication({
        optionsJSON: challengeData
      });

      // Verify unlock
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('webauthn-authenticate', {
        body: {
          action: 'unlock',
          credentialId: credential.id,
          hostname: window.location.hostname,
        }
      });

      if (verifyError || !verifyData?.success) {
        throw new Error('Unlock failed');
      }

      return true;
    } catch (error: any) {
      console.error('Biometric unlock error:', error);
      return false;
    }
  }, [user, isSupported, credentials]);

  // Delete a passkey
  const deletePasskey = useCallback(async (credentialId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('webauthn_credentials')
        .delete()
        .eq('id', credentialId);

      if (error) throw error;

      // Update local state
      setCredentials(prev => prev.filter(c => c.id !== credentialId));

      // If no more credentials, disable passkey settings
      if (credentials.length <= 1) {
        await updateSettings({
          passkey_enabled: false,
          biometric_enabled: false,
          biometric_unlock_enabled: false,
          biometric_login_enabled: false,
        });
      }

      toast.success('Passkey removed');
      return true;
    } catch (error) {
      console.error('Error deleting passkey:', error);
      toast.error('Failed to remove passkey');
      return false;
    }
  }, [credentials.length]);

  // Update security settings
  const updateSettings = useCallback(async (newSettings: Partial<SecuritySettings>): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_security_settings')
        .upsert({
          user_id: user.id,
          ...settings,
          ...newSettings,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setSettings(prev => prev ? { ...prev, ...newSettings } : null);
      return true;
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
      return false;
    }
  }, [user, settings]);

  return {
    credentials,
    settings,
    isLoading,
    isSupported,
    hasPlatformAuth,
    registerPasskey,
    authenticateWithPasskey,
    biometricUnlock,
    deletePasskey,
    updateSettings,
    refreshData: fetchData,
  };
};
